import { Brand, BrandDaysInLab } from 'API';
import { useCallback, useMemo } from 'react';
import ConfigService from 'shared/services/config.service';

interface CalculateDaysInLabParams {
  attributeNames: string[];
  productionFacility: string;
  isDigitalOrder: boolean;
  productCode: string;
}

interface CalculateTotalDaysInLabParams extends CalculateDaysInLabParams {
  returnNullIfExist?: boolean;
}

interface GetAttributeLevelDaysInLabParams {
  attributeName: string;
  productionFacility: string;
  isDigitalOrder: boolean;
  productCode: string;
}

/**
 * @param productBrandsResult - The product brands response from the API.
 */
export const useDaysInLab = (productBrandsResult: Record<string, Brand[]>) => {
  const daysInLabAttributeRecord = useMemo(() => {
    const result: { [key: string]: Record<string, Record<string, BrandDaysInLab>> } = {};
    Object.entries(productBrandsResult).forEach(([productCode, productBrands]) => {
      productBrands.forEach(brand => {
        const brandProduct = brand.products.find(product => product.productCode === productCode);
        if (!brandProduct) return;
        if (!result[productCode]) {
          result[productCode] = {};
        }
        result[productCode][brand.brandName.toLowerCase()] = brandProduct.attributes.reduce((acc, attribute) => {
          if (attribute.daysInLab) {
            acc[attribute.name.toLowerCase()] = attribute.daysInLab;
          }
          return acc;
        }, {} as Record<string, BrandDaysInLab>);
      });
    });
    return result;
  }, [productBrandsResult]);

  /**
   * Returns the days in lab for a target production facility based on the matching product in the productBrands response and the order type (i.e. digital).
   * @param productionFacility - The target production facility.
   * @returns the days in lab for the target production facility.
   */
  const getProductLevelDaysInLab = useCallback(
    (params: Omit<GetAttributeLevelDaysInLabParams, 'attributeName'>) => {
      const { productionFacility, isDigitalOrder, productCode } = params;
      if (!productionFacility || !productCode) return 0;
      const productBrands = productBrandsResult[productCode];
      if (!productBrands) return 0;
      const productBrand = productBrands.find(b => b.brandName.toLowerCase() === productionFacility.toLowerCase());
      const targetProduct = productBrand?.products.find(product => product.productCode === productCode);
      const daysInLabOptions = targetProduct?.daysInLab;
      // Use DefaultDaysInLab in Settings if no days in lab is set for the product. See comment from Quenton in LMS1-6335.
      const defaultDaysInLab = Number(ConfigService.getConfigValue('Settings', 'DefaultDaysInLab')) || 0;
      if (isDigitalOrder) return daysInLabOptions?.digital ?? defaultDaysInLab;
      return daysInLabOptions?.external ?? defaultDaysInLab;
    },
    [productBrandsResult]
  );

  /**
   * Returns the days in lab for a target production facility based on the matching product in the productBrands response and the order type (i.e. digital).
   * @param item - The target product attribute.
   * @param productionFacility - The target production facility.
   * @returns the days in lab for the target production facility.
   */
  const getAttributeLevelDaysInLab = useCallback(
    (params: GetAttributeLevelDaysInLabParams) => {
      const { attributeName, productionFacility, isDigitalOrder, productCode } = params;
      if (!attributeName || !productionFacility) return 0;
      const productionFacilityLowerCase = productionFacility.toLowerCase();
      const itemName = attributeName.toLowerCase();
      const targetAttribute = daysInLabAttributeRecord[productCode]?.[productionFacilityLowerCase]?.[itemName];
      // As per team conversation with Mazen, Quenton and Di, digital orders should use the internal field for days in lab.
      // Internal is being used in the place of the digital field, which we currently would not be able to set in Product Admin.
      const daysInLab = targetAttribute?.[isDigitalOrder ? 'internal' : 'external'] ?? null;
      return daysInLab;
    },
    [daysInLabAttributeRecord]
  );

  /**
   * Returns the total days in lab for a list of product attributes based on the matching product in the productBrands response and the order type (i.e. digital).
   * @param items - The list of product attributes.
   * @param productionFacility - The target production facility.
   * @returns the total days in lab for the target production facility.
   */
  const calculateTotalDaysInLab = useCallback(
    (params: CalculateTotalDaysInLabParams) => {
      const { attributeNames, productionFacility, isDigitalOrder, returnNullIfExist = false, productCode } = params;
      let nullItemCount = 0;
      const total = attributeNames.reduce((acc, attributeName) => {
        const daysInLab = getAttributeLevelDaysInLab({
          attributeName,
          productionFacility,
          isDigitalOrder,
          productCode,
        });
        if (daysInLab === null) {
          nullItemCount++;
          return acc;
        }
        return acc + daysInLab;
      }, 0);

      return returnNullIfExist ? (nullItemCount === attributeNames.length ? null : total) : total;
    },
    [getAttributeLevelDaysInLab]
  );

  /**
   * Returns the max days in lab for a list of product attributes based on the matching product in the productBrands response and the order type (i.e. digital).
   * @param items - The list of product attributes.
   * @param productionFacility - The target production facility.
   * @returns the max days in lab for the target production facility.
   */
  const calculateMaxDaysInLab = useCallback(
    (params: CalculateDaysInLabParams) => {
      const { attributeNames, productionFacility, isDigitalOrder, productCode } = params;
      const mappedItems = attributeNames.map(attributeName =>
        getAttributeLevelDaysInLab({ attributeName, productionFacility, isDigitalOrder, productCode })
      );
      if (!mappedItems.length) return null;
      return Math.max(...mappedItems);
    },
    [getAttributeLevelDaysInLab]
  );

  return {
    daysInLabAttributeRecord,
    getProductLevelDaysInLab,
    getAttributeLevelDaysInLab,
    calculateTotalDaysInLab,
    calculateMaxDaysInLab,
  };
};
